import {
  LayoutDashboard,
  Wrench,
  FileText,
  PieChart,
  Key,
  Calculator,
  Building,
  Settings,
  Briefcase,
  HeartHandshake,
  MessageSquare,
  ShieldCheck,
  StickyNote,
  Wallet,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { UserRole, ROUTES } from '@repo/shared-types';
import { getNavPathOverrideMatchers } from './navPathOverrides';

/** Bump when nav tree shape or resolver contract changes. */
export const NAV_SCHEMA_VERSION = '2026-05-28' as const;

export type NavBadgeKey =
  | 'maintenance.activeTickets'
  | 'receivables.overdue'
  | 'distributions.pending'
  | 'banking.unmatched';

/** Leaf definition in role trees (parentSection injected at resolve time). */
export type NavLeafItemDef = {
  id: string;
  label: string;
  route: string;
  icon?: LucideIcon;
  roles?: UserRole[];
  /** Schema keys resolved via {@link NavSchemaContext.orgFeatures} (not PostHog in Phase 1). */
  featureFlags?: string[];
  badgeKey?: NavBadgeKey;
};

/** Resolved leaf with parent section for active-state highlighting. */
export type NavLeafItem = NavLeafItemDef & {
  parentSection: string;
};

export type NavSection = {
  id: string;
  label: string;
  icon: LucideIcon;
  category: string;
  items?: NavLeafItemDef[];
  featureFlags?: string[];
};

export type NavSchemaContext = {
  readonly isSelfManagingOwner?: boolean;
  readonly orgFeatures?: {
    readonly hasCapTable?: boolean;
    readonly sfrOnlyPortfolio?: boolean;
  };
};

export type NavItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  category?: string;
  route?: string;
  children?: NavLeafItem[];
};

type RoleNavSchema = Record<UserRole, NavSection[]>;

/** Section after context filter with resolved leaf parentSection ids. */
type NavSectionResolved = Omit<NavSection, 'items'> & { items?: NavLeafItem[] };

const CAP_TABLE_FEATURE = 'cap_table';
const MULTIFAMILY_PORTFOLIO_FEATURE = 'multifamily_portfolio';

function normalizeSectionLeaves(section: NavSection): NavSectionResolved {
  const items = section.items;
  if (!items?.length) {
    return {
      id: section.id,
      label: section.label,
      icon: section.icon,
      category: section.category,
      featureFlags: section.featureFlags,
      items: undefined,
    };
  }
  return {
    ...section,
    items: items.map(
      (item): NavLeafItem => ({
        ...item,
        parentSection: section.id,
      }),
    ),
  };
}

function isFeatureVisible(featureFlags: string[] | undefined, context?: NavSchemaContext): boolean {
  if (!featureFlags?.length) return true;
  for (const flag of featureFlags) {
    if (flag === CAP_TABLE_FEATURE && context?.orgFeatures?.hasCapTable === false) return false;
    if (flag === MULTIFAMILY_PORTFOLIO_FEATURE && context?.orgFeatures?.sfrOnlyPortfolio === true) {
      return false;
    }
  }
  return true;
}

function filterSectionsByContext(sections: NavSection[], context?: NavSchemaContext): NavSection[] {
  return sections
    .filter((section) => isFeatureVisible(section.featureFlags, context))
    .map((section) => {
      const items = (section.items ?? []).filter((item) => isFeatureVisible(item.featureFlags, context));
      return { ...section, items };
    })
    .filter((section) => (section.items?.length ?? 0) > 0 || !section.items);
}

/** External PM workspace: full portfolio, leasing, accounting, and ops surfaces. */
const PM_NAV: NavSection[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    category: 'General',
    items: [
      { id: 'dashboard.overview', label: 'Overview', route: ROUTES.APP },
      { id: 'dashboard.activity', label: 'Activity', route: ROUTES.APP_ACTIVITY },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Zap,
    category: 'Management',
    items: [
      { id: 'work_queue.list', label: 'Work Queue', route: ROUTES.PM_WORK_QUEUE },
      { id: 'exceptions.list', label: 'Exceptions', route: ROUTES.PM_EXCEPTIONS },
      { id: 'pm.calendar.master', label: 'Operations Agenda', route: ROUTES.PM_CALENDAR },
      { id: 'workflows.list', label: 'Workflows', route: ROUTES.WORKFLOWS },
    ],
  },
  {
    id: 'leasing',
    label: 'Leasing',
    icon: Key,
    category: 'Management',
    items: [
      { id: 'leasing.metrics', label: 'Metrics', route: ROUTES.LEASING },
      { id: 'leasing.guest_cards', label: 'Guest Cards', route: ROUTES.GUEST_CARDS },
      { id: 'leasing.applications', label: 'Applications', route: ROUTES.APPLICATIONS },
      { id: 'leasing.leases', label: 'Leases', route: ROUTES.LEASES },
      { id: 'leasing.renewals', label: 'Renewals', route: ROUTES.LEASING_RENEWALS },
      { id: 'leasing.onboarding_checklists', label: 'Onboarding', route: ROUTES.LEASING_ONBOARDING },
      { id: 'leasing.marketing', label: 'Marketing', route: ROUTES.MARKETING },
    ],
  },
  {
    id: 'maintenance',
    label: 'Maintenance',
    icon: Wrench,
    category: 'Management',
    items: [
      { id: 'maintenance.work_orders', label: 'Work Orders', route: ROUTES.MAINTENANCE },
      { id: 'maintenance.turn_board', label: 'Unit Turns', route: ROUTES.TURNS },
      { id: 'maintenance.projects', label: 'Projects', route: ROUTES.PROJECTS },
      { id: 'maintenance.inspection_schedules', label: 'Inspections', route: ROUTES.INSPECTION_SCHEDULES },
      { id: 'maintenance.sops', label: 'SOP Templates', route: ROUTES.SOPS },
    ],
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    icon: Building,
    category: 'Portfolio',
    items: [
      { id: 'properties.directory', label: 'Properties', route: ROUTES.PROPERTIES },
      { id: 'owners.management', label: 'Owners', route: ROUTES.OWNERS_MANAGEMENT },
      {
        id: 'properties.units',
        label: 'Units',
        route: ROUTES.PROPERTY_UNITS,
        featureFlags: [MULTIFAMILY_PORTFOLIO_FEATURE],
      },
      { id: 'properties.portfolios', label: 'Portfolios', route: ROUTES.PROPERTY_PORTFOLIOS },
      { id: 'tenants.directory', label: 'Residents', route: ROUTES.TENANTS },
      { id: 'tenants.community', label: 'Community', route: ROUTES.COMMUNITY },
      { id: 'vendors.directory', label: 'Vendors', route: ROUTES.VENDORS },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: ShieldCheck,
    category: 'Portfolio',
    items: [
      { id: 'compliance.overview', label: 'Overview', route: ROUTES.COMPLIANCE },
      { id: 'compliance.inspections', label: 'Inspections', route: ROUTES.COMPLIANCE_INSPECTIONS },
      { id: 'compliance.hcv', label: 'HCV / Subsidized', route: ROUTES.COMPLIANCE_HCV },
      { id: 'compliance.obligations', label: 'Obligations', route: ROUTES.COMPLIANCE_OBLIGATIONS },
      { id: 'governance.logs', label: 'Governance Logs', route: ROUTES.COMPLIANCE_GOVERNANCE },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    icon: Calculator,
    category: 'Financials',
    items: [
      { id: 'accounting.payments', label: 'Receivables', route: ROUTES.RECEIVABLES },
      { id: 'accounting.bills', label: 'Bills & Invoices', route: ROUTES.OPS_ACCOUNTING_BILLS },
      { id: 'accounting.recurring_bills', label: 'Recurring Bills', route: ROUTES.RECURRING_BILLS },
      { id: 'accounting.gl', label: 'Ledger', route: ROUTES.GL },
      { id: 'accounting.coa', label: 'Chart of Accounts', route: ROUTES.COA },
      { id: 'accounting.reconciliation', label: 'Banking', route: ROUTES.RECONCILIATION },
      { id: 'accounting.cam', label: 'CAM', route: ROUTES.CAM },
      { id: 'accounting.management_fee_sweep', label: 'Management Fee Sweep', route: ROUTES.ACCOUNTING_MANAGEMENT_FEE_SWEEP },
      {
        id: 'accounting.owner_distribution',
        label: 'Owner Distributions',
        route: ROUTES.ACCOUNTING_OWNER_DISTRIBUTION,
        featureFlags: [CAP_TABLE_FEATURE],
      },
      { id: 'accounting.close', label: 'Month-End Close', route: ROUTES.CLOSE },
    ],
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: PieChart,
    category: 'Financials',
    items: [
      { id: 'reporting.library', label: 'Report Library', route: ROUTES.REPORTING },
      { id: 'reporting.scheduled', label: 'Scheduled Reports', route: ROUTES.REPORTING_SCHEDULED },
      { id: 'reporting.owner_statements', label: 'Owner Statements', route: ROUTES.OWNER_STATEMENTS },
    ],
  },
  {
    id: 'communications',
    label: 'Communications',
    icon: MessageSquare,
    category: 'Support',
    items: [{ id: 'communications.inbox', label: 'Messages', route: ROUTES.COMMUNICATIONS }],
  },
  {
    id: 'notes',
    label: 'Notes',
    icon: StickyNote,
    category: 'Support',
    items: [{ id: 'notes.list', label: 'Notes', route: ROUTES.NOTES }],
  },
];

/**
 * First-class simplified nav for self-managing owners (still `UserRole.PROPERTY_MANAGER` + `isSelfManagingOwner`).
 * Reuses stable leaf ids from {@link PM_NAV} so `getActiveNavIdFromPathRegistry` and `getRouteFromViewState` stay aligned.
 *
 * Phase 0: SMO nav omits external PM-only owner directory (`/app/owners`); self-serve owner distributions
 * and `/app/owner/*` workspace remain in-tree. See authorization.canAccessRoute.smo.test.ts.
 */
const SMO_PM_NAV: NavSection[] = [
  {
    id: 'smo_home',
    label: 'At a glance',
    icon: LayoutDashboard,
    category: 'My business',
    items: [
      { id: 'reporting.owner_statements', label: 'Statements & rent', route: ROUTES.OWNER_STATEMENTS },
      { id: 'properties.directory', label: 'My properties', route: ROUTES.PROPERTIES },
      { id: 'work_queue.list', label: 'Work queue', route: ROUTES.PM_WORK_QUEUE },
    ],
  },
  {
    id: 'leasing',
    label: 'Leasing',
    icon: Key,
    category: 'Residents',
    items: [
      { id: 'leasing.metrics', label: 'Pipeline', route: ROUTES.LEASING },
      { id: 'leasing.guest_cards', label: 'Guest cards', route: ROUTES.GUEST_CARDS },
      { id: 'leasing.applications', label: 'Applications', route: ROUTES.APPLICATIONS },
      { id: 'leasing.leases', label: 'Leases', route: ROUTES.LEASES },
      { id: 'leasing.renewals', label: 'Renewals', route: ROUTES.LEASING_RENEWALS },
      { id: 'leasing.onboarding_checklists', label: 'Move-in checklists', route: ROUTES.LEASING_ONBOARDING },
    ],
  },
  {
    id: 'maintenance',
    label: 'Maintenance & vendors',
    icon: Wrench,
    category: 'Operations',
    items: [
      { id: 'maintenance.work_orders', label: 'Work orders', route: ROUTES.MAINTENANCE },
      { id: 'maintenance.turn_board', label: 'Unit turns', route: ROUTES.TURNS },
      { id: 'maintenance.projects', label: 'Projects', route: ROUTES.PROJECTS },
      { id: 'maintenance.inspection_schedules', label: 'Inspections', route: ROUTES.INSPECTION_SCHEDULES },
      { id: 'vendors.directory', label: 'Vendors', route: ROUTES.VENDORS },
      { id: 'maintenance.sops', label: 'SOP templates', route: ROUTES.SOPS },
    ],
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    icon: Building,
    category: 'Portfolio',
    items: [
      { id: 'properties.units', label: 'Units', route: ROUTES.PROPERTY_UNITS },
      { id: 'tenants.directory', label: 'Residents', route: ROUTES.TENANTS },
      { id: 'properties.portfolios', label: 'Portfolios', route: ROUTES.PROPERTY_PORTFOLIOS },
    ],
  },
  {
    id: 'accounting',
    label: 'Money in / money out',
    icon: Calculator,
    category: 'Financials',
    items: [
      { id: 'accounting.payments', label: 'Receivables', route: ROUTES.RECEIVABLES },
      { id: 'accounting.bills', label: 'Bills & payables', route: ROUTES.OPS_ACCOUNTING_BILLS },
      { id: 'accounting.recurring_bills', label: 'Recurring bills', route: ROUTES.RECURRING_BILLS },
      { id: 'accounting.reconciliation', label: 'Banking', route: ROUTES.RECONCILIATION },
      { id: 'accounting.close', label: 'Month-end close', route: ROUTES.CLOSE },
    ],
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: PieChart,
    category: 'Financials',
    items: [{ id: 'reporting.library', label: 'Report library', route: ROUTES.REPORTING }],
  },
  {
    id: 'communications',
    label: 'Communications',
    icon: MessageSquare,
    category: 'Support',
    items: [{ id: 'communications.inbox', label: 'Messages', route: ROUTES.COMMUNICATIONS }],
  },
  {
    id: 'notes',
    label: 'Notes',
    icon: StickyNote,
    category: 'Support',
    items: [{ id: 'notes.list', label: 'Notes', route: ROUTES.NOTES }],
  },
];

/** Passive-owner visibility for PM-managed properties Casey still owns (no SMO ops). */
const SMO_PASSIVE_PM_MANAGED_NAV: NavSection[] = [
  {
    id: 'smo_pm_managed',
    label: 'PM-managed properties',
    icon: Building,
    category: 'Portfolio',
    items: [
      { id: 'smo_passive.properties', label: 'My PM-managed properties', route: `${ROUTES.OWNER_PROPERTIES}?propertyScope=passive-owned` },
      { id: 'smo_passive.statements', label: 'Statements', route: `${ROUTES.OWNER_STATEMENTS}?propertyScope=passive-owned` },
      { id: 'smo_passive.maintenance', label: 'Maintenance status', route: '/app/maintenance/completed?propertyScope=passive-owned' },
      { id: 'smo_passive.documents', label: 'Documents', route: `${ROUTES.OWNER_DOCUMENTS}?propertyScope=passive-owned` },
      { id: 'smo_passive.reporting', label: 'Performance reports', route: '/app/owner/reporting?propertyScope=passive-owned' },
      { id: 'smo_passive.communications', label: 'Messages to PM', route: `${ROUTES.OWNER_COMMUNICATION}?propertyScope=passive-owned` },
    ],
  },
];

const ADMIN_SETTINGS_SECTION: NavSection = {
  id: 'settings',
  label: 'System Settings',
  icon: Settings,
  category: 'System',
  items: [
    { id: 'settings.users', label: 'User Management', route: '/app/settings' },
    { id: 'settings.integrations', label: 'Integrations', route: ROUTES.INTEGRATIONS },
    { id: 'governance.logs', label: 'Governance Logs', route: '/app/compliance/governance' },
  ],
};

/** Platform operators: minimal shell (matches unrestricted RLS in API). */
const PLATFORM_OPERATOR_NAV: NavSection[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, category: 'General', items: [{ id: 'admin.dashboard.overview', label: 'Dashboard', route: '/app' }] },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    category: 'System',
    items: [
      { id: 'settings.users', label: 'User Management', route: '/app/settings' },
      { id: 'settings.integrations', label: 'Integrations', route: ROUTES.INTEGRATIONS },
      { id: 'governance.logs', label: 'Governance Logs', route: '/app/compliance/governance' },
    ],
  },
];

const NAV_SCHEMA: RoleNavSchema = {
  [UserRole.ADMIN]: [...PM_NAV, ADMIN_SETTINGS_SECTION],
  [UserRole.PROPERTY_MANAGER]: PM_NAV,
  [UserRole.TENANT]: [
    { id: 'tenant.my_home', label: 'My Home', icon: LayoutDashboard, category: 'General', items: [{ id: 'tenant.my_home.item', label: 'My Home', route: '/app/my-home' }] },
    {
      id: 'communications',
      label: 'Messages',
      icon: MessageSquare,
      category: 'Support',
      items: [{ id: 'tenant.communications', label: 'Messages', route: ROUTES.MY_COMMUNICATION }],
    },
    { id: 'tenant.payments', label: 'Payments', icon: Wallet, category: 'Financials', items: [{ id: 'tenant.payments.item', label: 'Payments', route: ROUTES.MY_PAYMENTS }] },
    {
      id: 'community',
      label: 'Community',
      icon: HeartHandshake,
      category: 'Social',
      items: [{ id: 'community.feed', label: 'Community', route: ROUTES.COMMUNITY }],
    },
    {
      id: 'maintenance.work_orders',
      label: 'Maintenance',
      icon: Wrench,
      category: 'Support',
      items: [{ id: 'maintenance.work_orders.item', label: 'Maintenance', route: ROUTES.MY_MAINTENANCE }],
    },
    { id: 'tenant.documents', label: 'Documents', icon: FileText, category: 'Resources', items: [{ id: 'tenant.documents.item', label: 'Documents', route: ROUTES.MY_DOCUMENTS }] },
  ],
  [UserRole.OWNER]: [
    { id: 'owners.dashboard', label: 'Dashboard', icon: LayoutDashboard, category: 'General', items: [{ id: 'owners.dashboard.overview', label: 'Dashboard', route: '/app/owner/dashboard' }] },
    { id: 'owners.properties', label: 'My Properties', icon: Building, category: 'Portfolio', items: [{ id: 'owners.properties.list', label: 'My Properties', route: '/app/owner/properties' }] },
    {
      id: 'owners.maintenance',
      label: 'Maintenance',
      icon: Wrench,
      category: 'Support',
      items: [
        { id: 'maintenance.work_orders', label: 'Active', route: '/app/maintenance' },
        { id: 'owners.maintenance.completed', label: 'Completed', route: '/app/maintenance/completed' },
      ],
    },
    {
      id: 'owners.financials',
      label: 'Financials',
      icon: Wallet,
      category: 'Financials',
      items: [
        { id: 'owners.statements', label: 'Statements', route: '/app/owner/statements' },
        { id: 'owners.reporting', label: 'Reports', route: '/app/owner/reporting' },
      ],
    },
    {
      id: 'compliance',
      label: 'Compliance',
      icon: ShieldCheck,
      category: 'Portfolio',
      items: [{ id: 'compliance.overview', label: 'Compliance', route: ROUTES.COMPLIANCE }],
    },
    {
      id: 'communications',
      label: 'Messages',
      icon: MessageSquare,
      category: 'Support',
      items: [{ id: 'owners.communications', label: 'Messages', route: ROUTES.OWNER_COMMUNICATION }],
    },
    {
      id: 'owners.documents',
      label: 'Documents',
      icon: FileText,
      category: 'Resources',
      items: [{ id: 'owners.documents.list', label: 'Documents', route: ROUTES.OWNER_DOCUMENTS }],
    },
    {
      id: 'owners.settings',
      label: 'Settings',
      icon: Settings,
      category: 'Account',
      items: [{ id: 'owners.settings.item', label: 'Settings', route: ROUTES.OWNER_SETTINGS }],
    },
  ],
  // Launch-deferred: investor portal stripped from nav until launch work resumes.
  // See docs/PRODUCTION_STATUS.md. Components remain under apps/web/src/components/investor/.
  [UserRole.INVESTOR]: [],
  [UserRole.VENDOR]: [
    { id: 'vendor.dashboard', label: 'Dashboard', icon: LayoutDashboard, category: 'General', items: [{ id: 'vendor.dashboard.overview', label: 'Dashboard', route: '/app' }] },
    {
      id: 'vendor.jobs',
      label: 'Jobs',
      icon: Wrench,
      category: 'Work',
      items: [
        { id: 'vendor.jobs.overview', label: 'All jobs', route: '/app/vendor/jobs' },
        { id: 'vendor.jobs.active', label: 'Active', route: '/app/vendor/jobs/active' },
        { id: 'vendor.jobs.completed', label: 'Completed', route: '/app/vendor/jobs/completed' },
      ],
    },
    {
      id: 'vendor.financials',
      label: 'Payments',
      icon: Wallet,
      category: 'Money',
      items: [
        { id: 'vendor.financials.invoices', label: 'Invoices', route: '/app/vendor/financials/invoices' },
        { id: 'vendor.financials.payments', label: 'Payments', route: '/app/vendor/financials/payments' },
        { id: 'vendor.financials.earnings', label: 'Earnings', route: '/app/vendor/financials/earnings' },
      ],
    },
    {
      id: 'vendor.profile',
      label: 'Profile',
      icon: Briefcase,
      category: 'Account',
      items: [
        { id: 'vendor.profile.business', label: 'Business info', route: '/app/vendor/profile/business' },
        { id: 'vendor.profile.reviews', label: 'Reviews', route: '/app/vendor/profile/reviews' },
      ],
    },
    {
      id: 'communications',
      label: 'Messages',
      icon: MessageSquare,
      category: 'Support',
      items: [{ id: 'vendor.communications', label: 'Messages', route: ROUTES.VENDOR_COMMUNICATION }],
    },
    { id: 'settings', label: 'Settings', icon: Settings, category: 'Account', items: [{ id: 'vendor.settings', label: 'Settings', route: ROUTES.VENDOR_SETTINGS }] },
  ],
  [UserRole.SYSTEM_ADMIN]: PLATFORM_OPERATOR_NAV,
  [UserRole.PLATFORM_ADMIN]: PLATFORM_OPERATOR_NAV,
};

const roleList = Object.values(UserRole);

const navRouteMap = new Map<string, string>();
const navRoleMap = new Map<string, Set<UserRole>>();
const navItemKind = new Map<string, 'section' | 'item'>();
const navIdsInOrder: string[] = [];

function registerSmoNavInRoleMap(sections: NavSection[]): void {
  for (const section of sections) {
    if (!navRoleMap.has(section.id)) {
      navRoleMap.set(section.id, new Set());
      navIdsInOrder.push(section.id);
    }
    navItemKind.set(section.id, 'section');
    navRoleMap.get(section.id)?.add(UserRole.PROPERTY_MANAGER);
    if (!navRouteMap.has(section.id)) {
      navRouteMap.set(section.id, section.items?.[0]?.route ?? `/app/${section.id}`);
    }

    for (const item of section.items ?? []) {
      if (!navRoleMap.has(item.id)) {
        navRoleMap.set(item.id, new Set());
        navIdsInOrder.push(item.id);
      }
      navItemKind.set(item.id, 'item');
      navRoleMap.get(item.id)?.add(UserRole.PROPERTY_MANAGER);
      if (!navRouteMap.has(item.id)) {
        navRouteMap.set(item.id, item.route);
      }
    }
  }
}

for (const role of roleList) {
  const sections = NAV_SCHEMA[role] ?? [];
  for (const section of sections) {
    if (!navRoleMap.has(section.id)) {
      navRoleMap.set(section.id, new Set());
      navIdsInOrder.push(section.id);
    }
    navItemKind.set(section.id, 'section');
    navRoleMap.get(section.id)?.add(role);
    if (!navRouteMap.has(section.id)) {
      navRouteMap.set(section.id, section.items?.[0]?.route ?? `/app/${section.id}`);
    }

    for (const item of section.items ?? []) {
      if (!navRoleMap.has(item.id)) {
        navRoleMap.set(item.id, new Set());
        navIdsInOrder.push(item.id);
      }
      navItemKind.set(item.id, 'item');
      navRoleMap.get(item.id)?.add(role);
      if (!navRouteMap.has(item.id)) {
        navRouteMap.set(item.id, item.route);
      }
    }
  }
}

registerSmoNavInRoleMap(SMO_PM_NAV);
registerSmoNavInRoleMap(SMO_PASSIVE_PM_MANAGED_NAV);

const sortedRouteEntries = Array.from(navRouteMap.entries())
  .map(([id, route]) => ({
    id,
    route,
    kind: navItemKind.get(id) ?? 'item',
  }))
  .sort((a, b) => {
    if (b.route.length !== a.route.length) {
      return b.route.length - a.route.length;
    }
    if (a.kind !== b.kind) {
      return a.kind === 'item' ? -1 : 1;
    }
    return 0;
  });

const navPathOverrideMatchers = getNavPathOverrideMatchers();

export const getNavSchemaForRole = (role: UserRole, context?: NavSchemaContext): NavSection[] => {
  if (role === UserRole.PROPERTY_MANAGER && context?.isSelfManagingOwner === true) {
    return [...SMO_PM_NAV, ...SMO_PASSIVE_PM_MANAGED_NAV];
  }
  return NAV_SCHEMA[role] ?? NAV_SCHEMA[UserRole.PROPERTY_MANAGER];
};

export const getRouteFromViewState = (view: string): string =>
  navRouteMap.get(view) ?? '/app';

export const getAllowedRolesForNavId = (navId: string): UserRole[] =>
  Array.from(navRoleMap.get(navId) ?? []);

export const getAllNavIds = (): string[] => [...navIdsInOrder];

export const getActiveNavIdFromPathRegistry = (pathname: string): string => {
  const cleanPath = pathname.split('?')[0].split('#')[0];
  if (cleanPath === '/app') return 'dashboard.overview';

  const exact = sortedRouteEntries.find((entry) => entry.route === cleanPath);
  if (exact) return exact.id;

  for (const { test, navId } of navPathOverrideMatchers) {
    if (test(cleanPath)) return navId;
  }

  const prefix = sortedRouteEntries.find((entry) => {
    if (entry.route === '/app') return false;
    return cleanPath.startsWith(entry.route);
  });
  return prefix?.id ?? 'dashboard';
};

export const toSidebarNavFromSections = (schema: NavSectionResolved[]): NavItem[] =>
  schema.map((section) => ({
    id: section.id,
    label: section.label,
    icon: section.icon,
    category: section.category,
    children: section.items?.map((item) => ({
      id: item.id,
      label: item.label,
      route: item.route,
      parentSection: item.parentSection,
      badgeKey: item.badgeKey,
      icon: item.icon,
    })),
  }));

/** Sole public nav resolution API for layout, command palette, and tests. */
export const getNavItemsForRole = (role: UserRole, context?: NavSchemaContext): NavItem[] => {
  const raw = getNavSchemaForRole(role, context);
  const filtered = filterSectionsByContext(raw, context).map(normalizeSectionLeaves);
  return toSidebarNavFromSections(filtered);
};

export const getNavSidebarItemsForLayout = (
  role: UserRole,
  isSelfManagingOwner: boolean,
  context?: Omit<NavSchemaContext, 'isSelfManagingOwner'>,
): NavItem[] => getNavItemsForRole(role, { ...context, isSelfManagingOwner });

/** Literal `route` strings from every nav leaf (for drift tests vs {@link ROUTES}). */
export const collectNavigationSchemaLiteralRoutes = (): string[] => {
  const seen = new Set<string>();
  for (const role of roleList) {
    for (const section of NAV_SCHEMA[role] ?? []) {
      for (const item of section.items ?? []) {
        seen.add(item.route);
      }
    }
  }
  for (const section of [...SMO_PM_NAV, ...SMO_PASSIVE_PM_MANAGED_NAV]) {
    for (const item of section.items ?? []) {
      seen.add(item.route);
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
};

